import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

const FONT_SANS = "'Geist', 'SF Pro Display', -apple-system, sans-serif";
const FONT_MONO = "'Geist Mono', 'SF Mono', monospace";
const API = '/api/chamas';

export default function ChamaDisputes({ chamaId, onBack }) {
  const { T } = useTheme();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => { fetchDisputes(); }, [chamaId]);

  const fetchDisputes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/${chamaId}/disputes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      setDisputes(d.disputes || []);
    } finally { setLoading(false); }
  };

  const fileDispute = async () => {
    if (reason.trim().length < 20) return setMsg({ type: 'error', text: 'Please describe the dispute in at least 20 characters' });
    setSubmitting(true); setMsg(null);
    try {
      const res = await fetch(`${API}/${chamaId}/disputes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setMsg({ type: 'success', text: d.message });
      setReason(''); setShowForm(false);
      fetchDisputes();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSubmitting(false); }
  };

  const castVote = async (disputeId, supports_removal) => {
    try {
      const res = await fetch(`${API}/${chamaId}/disputes/${disputeId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ supports_removal }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      fetchDisputes();
    } catch (e) { alert(e.message); }
  };

  const statusColor = (s) => ({
  open:         T.gold,
  under_review: T.accent,
  resolved:     T.yes,
  dismissed:    T.textDim,
  warning:      T.gold,
}[s] || T.textMuted);

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12 };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: FONT_SANS, color: T.text }}>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>

      <nav style={{
        position: 'sticky', top: 0, zIndex: 100, height: 56,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px',
        background: `${T.bg}ee`, backdropFilter: 'blur(16px)', borderBottom: `1px solid ${T.border}`,
      }}>
        <button onClick={onBack} style={{
          fontSize: 13, color: T.textMuted, padding: '6px 14px', borderRadius: 8,
          border: `1px solid ${T.border}`, background: T.surface, cursor: 'pointer',
        }}>← Back</button>
        <button onClick={() => { setShowForm(!showForm); setMsg(null); }} style={{
          padding: '8px 18px', borderRadius: 8, border: 'none',
          background: '#f43f5e', color: '#fff', fontFamily: FONT_SANS,
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>{showForm ? 'Cancel' : 'File Dispute'}</button>
      </nav>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px 80px', animation: 'fadeIn 0.4s ease' }}>

        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.text, margin: '0 0 4px', letterSpacing: '-0.02em' }}>Disputes</h1>
          <p style={{ color: T.textMuted, fontSize: 14, margin: 0 }}>Report rogue admin behaviour or policy violations</p>
        </div>

        {showForm && (
          <div style={{ ...card, padding: 24, marginBottom: 20 }}>
            <h3 style={{ color: T.text, fontSize: 15, fontWeight: 600, margin: '0 0 16px' }}>File a Dispute</h3>
            <div style={{ background: 'rgba(244,63,94,0.07)', border: '1px solid rgba(244,63,94,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
              <p style={{ color: '#f43f5e', fontSize: 12, lineHeight: 1.6 }}>
                Disputes are reviewed by platform admins within 48 hours. Filing a false dispute may result in account action. If 66%+ of members vote for admin removal, the dispute is automatically escalated.
              </p>
            </div>
            <textarea
  value={reason}
  onChange={e => setReason(e.target.value)}
  placeholder="Describe the issue in detail. What happened? What rules were violated? (min 20 characters)"
  maxLength={1000}
  rows={5}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: T.surfaceHi, border: `1px solid ${T.border}`,
                borderRadius: 10, padding: '12px 14px', color: T.text,
                fontFamily: FONT_SANS, fontSize: 13, outline: 'none',
                resize: 'none', lineHeight: 1.6, marginBottom: 14,
              }}
            />
            {msg && (
              <div style={{
                marginBottom: 14, borderRadius: 8, padding: '10px 14px',
                background: msg.type === 'success' ? 'rgba(22,199,132,0.1)' : 'rgba(234,57,67,0.1)',
                border: `1px solid ${msg.type === 'success' ? T.yes : '#f43f5e'}30`,
                color: msg.type === 'success' ? T.yes : '#f43f5e', fontSize: 13,
              }}>{msg.text}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: T.textDim, fontSize: 11 }}>{reason.length}/1000 characters</span>
              <button onClick={fileDispute} disabled={submitting} style={{
                padding: '10px 20px', borderRadius: 9, border: 'none',
                background: '#f43f5e', color: '#fff', fontFamily: FONT_SANS,
                fontSize: 13, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1,
              }}>{submitting ? 'Filing...' : 'Submit Dispute'}</button>
            </div>
          </div>
        )}

        {msg && !showForm && (
          <div style={{
            marginBottom: 16, borderRadius: 8, padding: '10px 14px',
            background: msg.type === 'success' ? 'rgba(22,199,132,0.1)' : 'rgba(234,57,67,0.1)',
            border: `1px solid ${msg.type === 'success' ? T.yes : '#f43f5e'}30`,
            color: msg.type === 'success' ? T.yes : '#f43f5e', fontSize: 13,
          }}>{msg.text}</div>
        )}

        {loading ? (
          <div style={{ ...card, padding: 40, textAlign: 'center', color: T.textDim, fontSize: 14 }}>Loading...</div>
        ) : disputes.length === 0 ? (
          <div style={{ ...card, padding: 40, textAlign: 'center', color: T.textDim, fontSize: 14 }}>
            No disputes filed — chama is running smoothly
          </div>
        ) : disputes.map(d => {
          const statusLabel = {
            open:         'Open',
            under_review: 'Under Review',
            resolved:     'Resolved',
            dismissed:    'Dismissed',
            warning:      'Warning Issued',
          }[d.status] || d.status;

          return (
          <div key={d.id} style={{
            ...card, padding: 20, marginBottom: 12, position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: 0, left: 0, bottom: 0, width: 3,
              background: statusColor(d.status), borderRadius: '12px 0 0 12px',
            }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
                    padding: '2px 8px', borderRadius: 4,
                    background: `${statusColor(d.status)}18`,
                    color: statusColor(d.status),
                    border: `1px solid ${statusColor(d.status)}30`,
                    fontFamily: FONT_MONO,
                  }}>{statusLabel}</span>
                  <span style={{ color: T.textDim, fontSize: 11 }}>by {d.filed_by_username}</span>
                  <span style={{ color: T.textDim, fontSize: 11 }}>
                    · {new Date(d.created_at).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
{d.status === 'under_review' && (
  <div style={{
    background: T.accentDim, border: `1px solid ${T.accent}33`,
    borderRadius: 8, padding: '9px 14px', marginBottom: 8,
  }}>
    <p style={{ color: T.accent, fontSize: 12, fontWeight: 600, margin: 0 }}>
      🔍 Platform admins are reviewing this dispute. The chama wallet has been frozen pending their decision.
    </p>
  </div>

)}
                  
                <p style={{ color: T.text, fontSize: 13, lineHeight: 1.6, marginBottom: 8 }}>{d.reason}</p>
                {d.resolution && (
                  <div style={{
                    background: T.surfaceHi, border: `1px solid ${T.border}`,
                    borderRadius: 8, padding: '10px 14px',
                  }}>
                    <div style={{ color: T.textDim, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Platform Resolution</div>
                    <p style={{ color: T.textMuted, fontSize: 13 }}>{d.resolution}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Vote tally */}
            {d.status === 'open' && (
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: T.textDim, fontSize: 12 }}>
                    Member votes: {d.votes_for_removal} for removal / {d.total_votes} total
                  </span>
                  <span style={{ color: T.textDim, fontSize: 11, fontFamily: FONT_MONO }}>
                    Need 66% for auto-escalation
                  </span>
                </div>
                {d.my_vote === null || d.my_vote === undefined ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <button onClick={() => castVote(d.id, true)} style={{
                      padding: '9px', borderRadius: 9, cursor: 'pointer',
                      fontFamily: FONT_SANS, fontSize: 13, fontWeight: 600,
                      background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.3)',
                      color: '#f43f5e',
                    }}>Support Removal</button>
                    <button onClick={() => castVote(d.id, false)} style={{
                      padding: '9px', borderRadius: 9, cursor: 'pointer',
                      fontFamily: FONT_SANS, fontSize: 13, fontWeight: 600,
                      background: T.surfaceHi, border: `1px solid ${T.border}`,
                      color: T.textMuted,
                    }}>Oppose Removal</button>
                  </div>
                ) : (
                  <div style={{
                    background: T.surfaceHi, border: `1px solid ${T.border}`,
                    borderRadius: 8, padding: '9px 14px', color: T.textMuted, fontSize: 12, fontWeight: 500,
                  }}>
                    You voted to {d.my_vote ? 'support' : 'oppose'} removal
                  </div>
                )}
              </div>
            )}
          </div>
        );
        })}
        
      </div>
    </div>
  );
}